import { useCallback } from 'react';
import type { OperationDefinition } from '@/types/operations';
import { useFavoriteOperations } from '@/hooks/useFavorites';
import { FavoriteStarButton } from './FavoriteStarButton';

/**
 * Stable class name parents reference in CSS selectors to reveal this
 * button on `:hover` / `:focus-within`. Kept as a literal string so
 * parent styles can target it without makeStyles hash mangling.
 */
export const OPERATION_FAVORITE_REVEAL_CLASS = 'otto-op-favorite';

type OperationFavoriteButtonProps = {
  operation: OperationDefinition;
};

export const OperationFavoriteButton = ({ operation }: OperationFavoriteButtonProps) => {
  const { isFavoriteOperation, toggleFavoriteOperation } = useFavoriteOperations();
  const isFavorited = isFavoriteOperation(operation);

  const handleToggle = useCallback(
    () => toggleFavoriteOperation(operation),
    [operation, toggleFavoriteOperation],
  );

  const label = isFavorited
    ? `Remove ${operation.name} from favorites`
    : `Add ${operation.name} to favorites`;

  return (
    <FavoriteStarButton
      isFavorited={isFavorited}
      onToggle={handleToggle}
      label={label}
      revealClass={OPERATION_FAVORITE_REVEAL_CLASS}
      hiddenUntilHover
    />
  );
};

export default OperationFavoriteButton;
